/* eslint-disable immutable/no-mutation */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { productsApi } from "../../../services/products";
import { ProductItem } from "../../ProductItem/ProductItem";
import { NavigationButtons } from "../NavigationButtons/NavigationButtons";
import { LoadingSpinner } from "../../LoadingSpinner/LoadingSpinner";
import "./TagSection.scss";

const PRODUCTS_PER_PAGE = 16;
const SCROLL_THRESHOLD = 400; // px from the right edge to trigger loading

export const TagSection = ({
  tagSlug,
  title,
  appliedFilters,
  sectionIndex,
  sectionRefs,
}) => {
  const { i18n } = useTranslation("application");
  const isRTL = i18n.language === "ar";
  const dispatch = useDispatch();
  const containerRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);

  // ---- Refs to avoid stale closures in scroll handler ----
  const isLoadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const currentPageRef = useRef(1);

  // Keep refs in sync with state
  useEffect(() => { isLoadingMoreRef.current = isLoadingMore; }, [isLoadingMore]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

  // Track filters key to reset on filter change
  const filtersKey = useRef("");

  const fetchPage = useCallback(
    async (page, isInitial = false) => {
      if (isInitial) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
        isLoadingMoreRef.current = true;
      }

      try {
        const result = await dispatch(
          productsApi.endpoints.getTagProducts.initiate({
            tagSlug,
            currentPage: page,
            filterParams: appliedFilters,
            lang: i18n.language,
          })
        ).unwrap();

        if (result?.records?.length > 0) {
          if (isInitial) {
            setProducts(result.records);
          } else {
            setProducts((prev) => [...prev, ...result.records]);
          }
          const moreAvailable = result.records.length === PRODUCTS_PER_PAGE;
          setHasMore(moreAvailable);
          hasMoreRef.current = moreAvailable;
          setIsEmpty(false);
        } else {
          if (isInitial) {
            setProducts([]);
            setIsEmpty(true);
          }
          setHasMore(false);
          hasMoreRef.current = false;
        }
      } catch (err) {
        console.error(`Error fetching ${tagSlug} page ${page}:`, err);
        if (isInitial) {
          setProducts([]);
          setIsEmpty(true);
        }
        setHasMore(false);
        hasMoreRef.current = false;
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
        isLoadingMoreRef.current = false;
      }
    },
    [dispatch, tagSlug, appliedFilters, i18n.language]
  );

  // Reset and fetch page 1 when filters or language change
  useEffect(() => {
    const newKey = JSON.stringify({ appliedFilters, lang: i18n.language });
    if (filtersKey.current !== newKey) {
      filtersKey.current = newKey;
      setProducts([]);
      setCurrentPage(1);
      currentPageRef.current = 1;
      setHasMore(true);
      hasMoreRef.current = true;
      setIsEmpty(false);
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
      fetchPage(1, true);
    }
  }, [appliedFilters, i18n.language, fetchPage]);

  // Fetch next page when currentPage increments beyond 1
  useEffect(() => {
    if (currentPage > 1) {
      fetchPage(currentPage, false);
    }
  }, [currentPage, fetchPage]);

  // ---- Check if we need to load more (reusable) ----
  const checkAndLoadMore = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (isLoadingMoreRef.current || !hasMoreRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const scrollRight = scrollWidth - clientWidth - Math.abs(scrollLeft);

    if (scrollRight < SCROLL_THRESHOLD) {
      const nextPage = currentPageRef.current + 1;
      setCurrentPage(nextPage);
      currentPageRef.current = nextPage;
    }
  }, []);

  // ---- Scroll-based lazy loading ----
  // Re-attach whenever products change (container might newly appear)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScrollEvent = () => {
      checkAndLoadMore();
    };

    container.addEventListener("scroll", handleScrollEvent);
    return () => container.removeEventListener("scroll", handleScrollEvent);
  }, [products.length, checkAndLoadMore]);

  // ---- Navigation button scroll handler ----
  const handleScroll = useCallback(
    (direction) => {
      const container = containerRef.current;
      if (container) {
        const scrollAmount = 300;
        container.scrollBy({
          left: direction === "left" ? -scrollAmount : scrollAmount,
          behavior: "smooth",
        });

        // After smooth scroll completes, check if we need more
        setTimeout(() => {
          checkAndLoadMore();
        }, 500);
      }
    },
    [checkAndLoadMore]
  );

  // Assign ref for parent-level access
  useEffect(() => {
    if (sectionRefs && containerRef.current) {
      sectionRefs.current[sectionIndex] = containerRef.current;
    }
  }, [sectionRefs, sectionIndex, products.length]);

  // Don't render section at all if empty (no products for this tag)
  if (isEmpty && !isLoading) {
    return null;
  }

  // Show loading spinner while initial load is in progress
  if (isLoading && products.length === 0) {
    return (
      <section className="product-section tag-section">
        <div className="heading-section">
          <h2 className="section-heading">{title}</h2>
        </div>
        <div className="horizontal-container tag-section__loading">
          <LoadingSpinner />
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <section className="product-section tag-section">
      <div className="heading-section">
        <h2 className="section-heading">{title}</h2>
        <NavigationButtons
          onLeftClick={() => handleScroll("left")}
          onRightClick={() => handleScroll("right")}
          isRTL={isRTL}
        />
      </div>
      <div className="horizontal-container" ref={containerRef}>
        {products.map((product) => (
          <div className="horizontal-item" key={product.id}>
            <ProductItem
              key={`${i18n.language}-${tagSlug}-${product.id}`}
              product={product}
            />
          </div>
        ))}
        {isLoadingMore && (
          <div className="horizontal-item tag-section__load-more">
            <LoadingSpinner />
          </div>
        )}
      </div>
    </section>
  );
};
